import React, { useState } from 'react';
import PageHeader from '../components/layout/PageHeader';
import Tabs from '../components/ui/Tabs';
import Button from '../components/ui/Button';
import { ArrowRightIcon, CalendarIcon, MapPinIcon, ExternalLinkIcon, SearchIcon, FilterIcon, BriefcaseIcon, GraduationCapIcon, BuildingIcon, UserIcon, UsersIcon, TrophyIcon, HeartIcon } from 'lucide-react';
interface AlumniProfile {
  id: string;
  name: string;
  graduationYear: number;
  profession: string;
  company?: string;
  location: string;
  bio: string;
  achievements: string[];
  image: string;
  featured?: boolean;
}
interface AlumniEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  description: string;
  image: string;
}
const Alumni = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSet, setSelectedSet] = useState<string | null>(null);
  const [filteredAlumni, setFilteredAlumni] = useState<AlumniProfile[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  // Featured Alumni - Real distinguished alumni from executive committee
  const alumniProfiles: AlumniProfile[] = [{
    id: '1',
    name: 'Prof. Akintunde Otubu',
    graduationYear: 1989,
    profession: 'National President, IGS Alumni Association',
    company: 'Oil & Gas Sector',
    location: 'Lagos, Nigeria',
    bio: "A distinguished professional with extensive experience in the oil and gas sector. Prof. Otubu has led numerous multi-million dollar projects across West Africa and is passionate about giving back to his alma mater. As National President of the Alumni Association, he champions initiatives that enhance the school's infrastructure and academic excellence.",
    achievements: ['National President, IGS Alumni Association', 'Led multi-million dollar projects across West Africa', 'M.Sc. Engineering Management - Imperial College London', 'Over 25 years in oil and gas sector leadership'],
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jn44YtV68i3KkaTAFc7QtH/otubu.png',
    featured: true
  }, {
    id: '2',
    name: 'Elder Lanre Oyesile',
    graduationYear: 1981,
    profession: 'First Vice President, IGS Alumni Association',
    company: 'Larry Jay Properties Consultant',
    location: 'Lagos, Nigeria',
    bio: 'A seasoned professional with a distinguished career in public service and entrepreneurship. Elder Oyesile served with Surulere Local Government until his statutory retirement in 2019. He currently serves as MD/CEO of Larry Jay Properties Consultant, a division of Larry Jay Printers. His extensive experience in both public administration and private enterprise brings valuable leadership to the alumni association.',
    achievements: ['First Vice President, IGS Alumni Association', 'MD/CEO, Larry Jay Properties Consultant', 'Retired from Surulere Local Government (2019)', 'Extensive experience in public administration and private enterprise'],
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3Hew6rxsK47JVpej3qfHsv/WhatsApp_Image_2025-11-10_at_20.52.09.jpg',
    featured: true
  }, {
    id: '3',
    name: 'Olumide Tayo-Lawal, Esq.',
    graduationYear: 1987,
    profession: 'National General Secretary, IGS Alumni Association',
    company: 'Legal Practice',
    location: 'Ogun State, Nigeria',
    bio: 'A distinguished legal practitioner and community development advocate. Called to the Nigerian Bar in 2000, he has been in active legal practice while making significant contributions to politics and community development. As National General Secretary of the Alumni Association, he remains deeply passionate about the affairs of his alma mater.',
    achievements: ['National General Secretary, IGS Alumni Association', 'Called to Nigerian Bar (2000)', 'LL.B - University of Benin', 'Active in politics and community development'],
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5aaAcoc7u7GwjzMdDw1BU9/WhatsApp_Image_2025-11-09_at_14.48.47.jpg',
    featured: true
  }];
  // Alumni events - Using school images only
  const alumniEvents: AlumniEvent[] = [{
    id: '1',
    title: 'Annual Alumni Reunion',
    date: '2023-12-15',
    time: '12:00 PM - 6:00 PM',
    location: 'Isonyin Grammar School Campus',
    description: 'The annual reunion brings together alumni from all graduating classes for a day of networking, reminiscing, and reconnecting with the school. The event includes campus tours, cultural performances, and an evening dinner.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nn93xnwTcDMxPXw6huLouK/WhatsApp_Image_2025-08-14_at_15.45.40_%281%29.jpg'
  }, {
    id: '2',
    title: 'Alumni Career Fair',
    date: '2024-02-20',
    time: '10:00 AM - 4:00 PM',
    location: 'Isonyin Grammar School Hall',
    description: 'Alumni professionals from various fields return to share career insights with current students. The event includes panel discussions, one-on-one mentoring sessions, and networking opportunities.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3DHAcubmTqmT13YpTTNQmr/WhatsApp_Image_2025-10-24_at_10.01.24.jpg'
  }, {
    id: '3',
    title: 'Alumni Fundraising Gala',
    date: '2024-04-05',
    time: '7:00 PM - 11:00 PM',
    location: 'Radisson Blu Hotel, Lagos',
    description: 'An elegant evening of dining and entertainment to raise funds for the Alumni Scholarship Fund and other school development projects. The event includes an auction, awards ceremony, and special performances.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bZLbes2hxk7MhXzaZ1hXLV/WhatsApp_Image_2025-10-24_at_10.01.19.jpg'
  }, {
    id: '4',
    title: 'Class of 2000 Silver Jubilee',
    date: '2025-07-25',
    time: '2:00 PM - 10:00 PM',
    location: 'Isonyin Grammar School and Local Resort',
    description: 'A special celebration marking 25 years since the Class of 2000 graduated. The day begins with a thanksgiving service and campus visit, followed by a dinner and dance at a local resort.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/xwrtKazFgq7H6urQHRmWav/WhatsApp_Image_2025-10-24_at_10.01.12_%281%29.jpg'
  }];
  const graduationSets = [{
    decade: '1960s',
    years: [1967, 1968, 1969]
  }, {
    decade: '1970s',
    years: [1970, 1971, 1972, 1973, 1974, 1975, 1976, 1977, 1978, 1979]
  }, {
    decade: '1980s',
    years: [1980, 1981, 1982, 1983, 1984, 1985, 1986, 1987, 1988, 1989]
  }, {
    decade: '1990s',
    years: [1990, 1991, 1992, 1993, 1994, 1995, 1996, 1997, 1998, 1999]
  }, {
    decade: '2000s',
    years: [2000, 2001, 2002, 2003, 2004, 2005, 2006, 2007, 2008, 2009]
  }, {
    decade: '2010s',
    years: [2010, 2011, 2012, 2013, 2014, 2015, 2016, 2017, 2018, 2019]
  }, {
    decade: '2020s',
    years: [2020, 2021, 2022, 2023]
  }];
  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  return <div className="min-h-screen bg-white" data-id="element-1953">
      <PageHeader title="Alumni Network" description="Connect with our global network of distinguished alumni who continue to make significant contributions to society." backgroundImage="https://uploadthingy.s3.us-west-1.amazonaws.com/bZLbes2hxk7MhXzaZ1hXLV/WhatsApp_Image_2025-10-24_at_10.01.19.jpg" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Alumni',
      path: '/alumni'
    }]} data-id="element-1954" />

      <div className="container mx-auto px-4 py-12" data-id="element-1955">
        {/* Alumni Association Section */}
        <section className="mb-16" id="association" data-id="element-1956">
          <div className="text-center mb-10" data-id="element-1957">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-1958">
              Isonyin Grammar School Alumni Association
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-1959"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-1960">
              The Isonyin Grammar School Alumni Association (IGSAA) connects
              former students worldwide, fostering lifelong relationships and
              supporting the school's development.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-1961">
            <div data-id="element-1962">
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-1963">
                Founded in 1975, the IGSAA has grown into a vibrant global
                network of over 15,000 alumni who have gone on to make
                significant contributions in various fields including medicine,
                law, business, education, politics, arts, and sports.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-1964">
                The association serves as a platform for networking, mentorship,
                and giving back to the alma mater. Through regular events,
                reunions, and digital platforms, alumni stay connected and
                support each other's professional and personal growth.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-1965">
                IGSAA also plays a crucial role in the development of the school
                through fundraising initiatives, infrastructure projects,
                scholarship programs, and mentorship of current students. The
                association's contributions have significantly enhanced the
                quality of education and facilities at Isonyin Grammar School.
              </p>
              <div className="bg-blue-50 p-6 rounded-lg" data-id="element-1966">
                <h3 className="font-bold text-blue-900 mb-4" data-id="element-1967">Our Mission</h3>
                <ul className="space-y-3" data-id="element-1968">
                  <li className="flex items-start" data-id="element-1969">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-1970">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1971">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1972" />
                      </svg>
                    </div>
                    <span className="text-gray-700" data-id="element-1973">
                      To foster lifelong connections among alumni
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1974">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-1975">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1976">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1977" />
                      </svg>
                    </div>
                    <span className="text-gray-700" data-id="element-1978">
                      To support the development and growth of Isonyin Grammar
                      School
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1979">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-1980">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1981">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1982" />
                      </svg>
                    </div>
                    <span className="text-gray-700" data-id="element-1983">
                      To provide mentorship and guidance to current students
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1984">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-1985">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1986">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1987" />
                      </svg>
                    </div>
                    <span className="text-gray-700" data-id="element-1988">
                      To promote the values and reputation of the school in the
                      wider community
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div data-id="element-1989">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bZLbes2hxk7MhXzaZ1hXLV/WhatsApp_Image_2025-10-24_at_10.01.19.jpg" alt="Alumni gathering" className="w-full h-auto rounded-lg shadow-md mb-6" data-id="element-1990" />
              <div className="grid grid-cols-2 gap-4" data-id="element-1991">
                <div className="bg-blue-900 p-4 rounded-lg text-white text-center" data-id="element-1992">
                  <div className="text-3xl font-bold mb-1" data-id="element-1993">15,000+</div>
                  <p className="text-sm" data-id="element-1994">Alumni Worldwide</p>
                </div>
                <div className="bg-blue-900 p-4 rounded-lg text-white text-center" data-id="element-1995">
                  <div className="text-3xl font-bold mb-1" data-id="element-1996">48</div>
                  <p className="text-sm" data-id="element-1997">Years of IGSAA</p>
                </div>
                <div className="bg-blue-900 p-4 rounded-lg text-white text-center" data-id="element-1998">
                  <div className="text-3xl font-bold mb-1" data-id="element-1999">₦50M+</div>
                  <p className="text-sm" data-id="element-2000">Raised for School Projects</p>
                </div>
                <div className="bg-blue-900 p-4 rounded-lg text-white text-center" data-id="element-2001">
                  <div className="text-3xl font-bold mb-1" data-id="element-2002">25</div>
                  <p className="text-sm" data-id="element-2003">Global Chapters</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2004">
            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2005">
              <div className="flex items-center mb-4" data-id="element-2006">
                <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-2007">
                  <UsersIcon className="h-6 w-6 text-blue-700" data-id="element-2008" />
                </div>
                <h3 className="text-xl font-bold text-blue-900" data-id="element-2009">Membership</h3>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-2010">
                All former students of Isonyin Grammar School are automatically
                members of the Alumni Association. Active membership requires
                registration and payment of annual dues.
              </p>
              <Button href="/alumni/join" variant="outline" className="w-full" data-id="element-2011">
                Register as a Member
              </Button>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2012">
              <div className="flex items-center mb-4" data-id="element-2013">
                <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-2014">
                  <CalendarIcon className="h-6 w-6 text-blue-700" data-id="element-2015" />
                </div>
                <h3 className="text-xl font-bold text-blue-900" data-id="element-2016">
                  Events & Activities
                </h3>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-2017">
                The association organizes regular events including annual
                reunions, fundraising galas, career fairs, and mentorship
                programs. These events provide opportunities for networking and
                giving back.
              </p>
              <Button href="/alumni/events" variant="outline" className="w-full" data-id="element-2018">
                View Upcoming Events
              </Button>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2019">
              <div className="flex items-center mb-4" data-id="element-2020">
                <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-2021">
                  <HeartIcon className="h-6 w-6 text-blue-700" data-id="element-2022" />
                </div>
                <h3 className="text-xl font-bold text-blue-900" data-id="element-2023">Give Back</h3>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-2024">
                Alumni can support the school through donations, volunteering,
                mentorship, and participating in development projects. Your
                contribution helps ensure a bright future for current and future
                students.
              </p>
              <Button href="/support" variant="outline" className="w-full" data-id="element-2025">
                Support IGS
              </Button>
            </div>
          </div>

          {/* National Executive Committee Leadership */}
          <div className="mt-12 bg-blue-50 p-8 rounded-lg" data-id="element-2026">
            <div className="text-center mb-6" data-id="element-2027">
              <h3 className="text-2xl font-bold text-blue-900 mb-2" data-id="element-2028">
                Alumni Association Leadership
              </h3>
              <p className="text-gray-600" data-id="element-2029">
                Meet the dedicated individuals leading the Isonyin Grammar
                School Alumni Association
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2030">
              {/* National President */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2031">
                <div className="flex flex-col items-center" data-id="element-2032">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jn44YtV68i3KkaTAFc7QtH/otubu.png" alt="National President" className="w-32 h-32 rounded-full object-cover mb-4 border-4 border-yellow-500" data-id="element-2033" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2034">
                    Prof. Akintunde Otubu
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2035">
                    National President
                  </p>
                </div>
              </div>

              {/* First Vice President */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2036">
                <div className="flex flex-col items-center" data-id="element-2037">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3Hew6rxsK47JVpej3qfHsv/WhatsApp_Image_2025-11-10_at_20.52.09.jpg" alt="First Vice President" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2038" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2039">
                    Elder Lanre Oyesile
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2040">
                    First Vice President
                  </p>
                </div>
              </div>

              {/* National General Secretary */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2041">
                <div className="flex flex-col items-center" data-id="element-2042">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/5aaAcoc7u7GwjzMdDw1BU9/WhatsApp_Image_2025-11-09_at_14.48.47.jpg" alt="National General Secretary" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2043" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2044">
                    Olumide Tayo-Lawal, Esq.
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2045">
                    National General Secretary
                  </p>
                </div>
              </div>

              {/* National Treasurer */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2046">
                <div className="flex flex-col items-center" data-id="element-2047">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/w7eCBy2QnBuM2eaUMJCtJK/WhatsApp_Image_2025-11-09_at_12.59.17.jpg" alt="National Treasurer" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2048" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2049">
                    SADIQ Adebayo Rasaki
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2050">
                    National Treasurer
                  </p>
                </div>
              </div>

              {/* National Financial Secretary */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2051">
                <div className="flex flex-col items-center" data-id="element-2052">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xoPZ5CeiJfFxam6WebTFFc/WhatsApp_Image_2025-11-09_at_14.37.11.jpg" alt="National Financial Secretary" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2053" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2054">
                    Abimbola Taiwo Olarewaju
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2055">
                    National Financial Secretary
                  </p>
                </div>
              </div>

              {/* Publicity Secretary */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2056">
                <div className="flex flex-col items-center" data-id="element-2057">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gDiHx8kfPCYtVcBF6agkJD/WhatsApp_Image_2025-11-09_at_14.33.59.jpg" alt="Publicity Secretary" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2058" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2059">
                    Yeye Adesola Salami
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2060">
                    Publicity Secretary
                  </p>
                </div>
              </div>

              {/* Assistant General Secretary */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2061">
                <div className="flex flex-col items-center" data-id="element-2062">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nR1xAQu1SrTC6snxzfQ7dJ/image.png" alt="Assistant General Secretary" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2063" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2064">
                    Olamide Adesola Oyenuga
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2065">
                    Assistant General Secretary
                  </p>
                </div>
              </div>

              {/* National Social Director */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2066">
                <div className="flex flex-col items-center" data-id="element-2067">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4BW49KtAwcy4acgofnGhtA/1000770229.jpg" alt="National Social Director" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2068" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2069">
                    Orepekan Kehinde Adewale
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2070">
                    National Social Director
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-6 text-center" data-id="element-2071">
              <Button href="/alumni/excos" variant="outline" data-id="element-2072">
                View Full Executive Committee
              </Button>
            </div>
          </div>
        </section>

        {/* Featured Alumni Section */}
        <section className="mb-16" id="featured" data-id="element-2073">
          <div className="text-center mb-10" data-id="element-2074">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2075">
              Featured Alumni
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2076"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2077">
              Celebrating the achievements and contributions of our
              distinguished alumni who are making a difference in various fields
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2078">
            {alumniProfiles.filter(profile => profile.featured).map(alumni => <div key={alumni.id} className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-2079">
                  <div className="h-64 overflow-hidden" data-id="element-2080">
                    <img src={alumni.image} alt={alumni.name} className="w-full h-full object-cover" data-id="element-2081" />
                  </div>
                  <div className="p-6" data-id="element-2082">
                    <h3 className="text-xl font-bold text-blue-900 mb-1" data-id="element-2083">
                      {alumni.name}
                    </h3>
                    <div className="flex items-center text-gray-700 font-medium mb-3" data-id="element-2084">
                      <BriefcaseIcon className="h-4 w-4 mr-1" data-id="element-2085" />
                      <span data-id="element-2086">{alumni.profession}</span>
                    </div>
                    <div className="flex items-center text-gray-500 text-sm mb-4" data-id="element-2087">
                      <MapPinIcon className="h-4 w-4 mr-1" data-id="element-2088" />
                      <span data-id="element-2089">{alumni.location}</span>
                    </div>
                    <p className="text-gray-700 line-clamp-3" data-id="element-2090">{alumni.bio}</p>
                  </div>
                </div>)}
          </div>
        </section>

        {/* Alumni Contributions Section */}
        <section className="mb-16 bg-gray-50 py-12" data-id="element-2091">
          <div className="container mx-auto px-4" data-id="element-2092">
            <div className="text-center mb-10" data-id="element-2093">
              <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2094">
                Alumni Contributions
              </h2>
              <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2095"></div>
              <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2096">
                Our alumni continue to support the school through generous
                donations and facility upgrades
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden" data-id="element-2097">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jA5f2egHsPW6YcMhP4RtXv/WhatsApp_Image_2025-10-24_at_10.01.18_%281%29.jpg" alt="Chemistry Laboratory renovation sign by Old Students Association" className="w-full h-auto" data-id="element-2098" />
              <div className="p-6" data-id="element-2099">
                <h3 className="text-2xl font-bold text-blue-900 mb-3" data-id="element-2100">
                  Chemistry Laboratory Renovation
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-2101">
                  The Old Students Association 75/80 Set generously renovated
                  our Chemistry Laboratory, which was commissioned on January
                  27th, 2024. This state-of-the-art facility provides students
                  with an excellent environment for conducting chemistry
                  experiments and practical work.
                </p>
                <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-2102">
                  The renovation included new laboratory benches, modern
                  equipment, improved ventilation, and safety features that meet
                  international standards. The Senior Parents Club Association
                  also contributed to the provision of science equipment for
                  adequate student development.
                </p>
                <div className="bg-blue-50 p-6 rounded-lg mt-6" data-id="element-2103">
                  <h4 className="font-bold text-blue-900 mb-3" data-id="element-2104">
                    Other Notable Alumni Contributions
                  </h4>
                  <ul className="space-y-3" data-id="element-2105">
                    <li className="flex items-start" data-id="element-2106">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-2107">
                        <span className="text-blue-700 text-sm" data-id="element-2108">✓</span>
                      </div>
                      <span className="text-gray-700" data-id="element-2109">
                        ICT Laboratory donated by Old Students Association 75/80
                        Set
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-2110">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-2111">
                        <span className="text-blue-700 text-sm" data-id="element-2112">✓</span>
                      </div>
                      <span className="text-gray-700" data-id="element-2113">
                        Science equipment provided by Senior Parents Club
                        Association
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-2114">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-2115">
                        <span className="text-blue-700 text-sm" data-id="element-2116">✓</span>
                      </div>
                      <span className="text-gray-700" data-id="element-2117">
                        Over ₦50 million raised for school development projects
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-2118">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-2119">
                        <span className="text-blue-700 text-sm" data-id="element-2120">✓</span>
                      </div>
                      <span className="text-gray-700" data-id="element-2121">
                        Annual scholarship programs supporting deserving
                        students
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Alumni Directory Section */}
        <section className="mb-16" id="directory" data-id="element-2122">
          <div className="text-center mb-10" data-id="element-2123">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2124">
              Alumni Sets Directory
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2125"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2126">
              Find and connect with your classmates and other alumni by
              graduation year.
            </p>
          </div>
          <div className="flex flex-wrap items-center space-x-2 mb-6" data-id="element-2127">
            <div className="relative" data-id="element-2128">
              <input type="text" placeholder="Search alumni..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-2129" />
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-2130" />
            </div>
            <button onClick={() => setFilterOpen(!filterOpen)} className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2131">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-2132" />
              Filter by Set
            </button>
          </div>
          {filterOpen && <div className="bg-gray-50 p-4 rounded-lg mb-6 animate-fadeIn" data-id="element-2133">
              <h3 className="font-medium text-gray-900 mb-3" data-id="element-2134">
                Filter by Graduation Year
              </h3>
              <div className="space-y-4" data-id="element-2135">
                {graduationSets.map(set => <div key={set.decade} data-id="element-2136">
                    <h4 className="font-medium text-blue-900 mb-2" data-id="element-2137">
                      {set.decade}
                    </h4>
                    <div className="flex flex-wrap gap-2" data-id="element-2138">
                      {set.years.map(year => <button key={year} onClick={() => setSelectedSet(year.toString())} className={`px-3 py-1 rounded-full text-sm ${selectedSet === year.toString() ? 'bg-blue-900 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-id="element-2139">
                          {year}
                        </button>)}
                    </div>
                  </div>)}
                <div className="pt-2 border-t border-gray-200" data-id="element-2140">
                  <button onClick={() => setSelectedSet(null)} className="px-4 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm font-medium" data-id="element-2141">
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2142">
            {/* Sample class representatives using real alumni images */}
            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2143">
              <div className="flex items-center mb-4" data-id="element-2144">
                <div className="bg-blue-100 text-blue-900 font-bold rounded p-3 text-center min-w-[70px] mr-4" data-id="element-2145">
                  <div className="text-lg" data-id="element-2146">1989</div>
                  <div className="text-xs" data-id="element-2147">SET</div>
                </div>
                <div data-id="element-2148">
                  <h3 className="font-bold text-blue-900" data-id="element-2149">Class of 1989</h3>
                  <p className="text-sm text-gray-500" data-id="element-2150">125 registered alumni</p>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-2151">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jn44YtV68i3KkaTAFc7QtH/otubu.png" alt="Class Representative" className="w-12 h-12 rounded-full object-cover mr-3" data-id="element-2152" />
                <div data-id="element-2153">
                  <h4 className="font-medium text-gray-900" data-id="element-2154">
                    Prof. Akintunde Otubu
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-2155">Class Representative</p>
                </div>
              </div>
              <Button href="/alumni/sets" variant="outline" size="sm" fullWidth data-id="element-2156">
                View Class Directory
              </Button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2157">
              <div className="flex items-center mb-4" data-id="element-2158">
                <div className="bg-blue-100 text-blue-900 font-bold rounded p-3 text-center min-w-[70px] mr-4" data-id="element-2159">
                  <div className="text-lg" data-id="element-2160">1987</div>
                  <div className="text-xs" data-id="element-2161">SET</div>
                </div>
                <div data-id="element-2162">
                  <h3 className="font-bold text-blue-900" data-id="element-2163">Class of 1987</h3>
                  <p className="text-sm text-gray-500" data-id="element-2164">118 registered alumni</p>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-2165">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/5aaAcoc7u7GwjzMdDw1BU9/WhatsApp_Image_2025-11-09_at_14.48.47.jpg" alt="Class Representative" className="w-12 h-12 rounded-full object-cover mr-3" data-id="element-2166" />
                <div data-id="element-2167">
                  <h4 className="font-medium text-gray-900" data-id="element-2168">
                    Olumide Tayo-Lawal, Esq.
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-2169">Class Representative</p>
                </div>
              </div>
              <Button href="/alumni/sets" variant="outline" size="sm" fullWidth data-id="element-2170">
                View Class Directory
              </Button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2171">
              <div className="flex items-center mb-4" data-id="element-2172">
                <div className="bg-blue-100 text-blue-900 font-bold rounded p-3 text-center min-w-[70px] mr-4" data-id="element-2173">
                  <div className="text-lg" data-id="element-2174">1986</div>
                  <div className="text-xs" data-id="element-2175">SET</div>
                </div>
                <div data-id="element-2176">
                  <h3 className="font-bold text-blue-900" data-id="element-2177">Class of 1986</h3>
                  <p className="text-sm text-gray-500" data-id="element-2178">132 registered alumni</p>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-2179">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gDiHx8kfPCYtVcBF6agkJD/WhatsApp_Image_2025-11-09_at_14.33.59.jpg" alt="Class Representative" className="w-12 h-12 rounded-full object-cover mr-3" data-id="element-2180" />
                <div data-id="element-2181">
                  <h4 className="font-medium text-gray-900" data-id="element-2182">
                    Yeye Adesola Salami
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-2183">Class Representative</p>
                </div>
              </div>
              <Button href="/alumni/sets" variant="outline" size="sm" fullWidth data-id="element-2184">
                View Class Directory
              </Button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2185">
              <div className="flex items-center mb-4" data-id="element-2186">
                <div className="bg-blue-100 text-blue-900 font-bold rounded p-3 text-center min-w-[70px] mr-4" data-id="element-2187">
                  <div className="text-lg" data-id="element-2188">1983</div>
                  <div className="text-xs" data-id="element-2189">SET</div>
                </div>
                <div data-id="element-2190">
                  <h3 className="font-bold text-blue-900" data-id="element-2191">Class of 1983</h3>
                  <p className="text-sm text-gray-500" data-id="element-2192">105 registered alumni</p>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-2193">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/w7eCBy2QnBuM2eaUMJCtJK/WhatsApp_Image_2025-11-09_at_12.59.17.jpg" alt="Class Representative" className="w-12 h-12 rounded-full object-cover mr-3" data-id="element-2194" />
                <div data-id="element-2195">
                  <h4 className="font-medium text-gray-900" data-id="element-2196">
                    SADIQ Adebayo Rasaki
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-2197">Class Representative</p>
                </div>
              </div>
              <Button href="/alumni/sets" variant="outline" size="sm" fullWidth data-id="element-2198">
                View Class Directory
              </Button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2199">
              <div className="flex items-center mb-4" data-id="element-2200">
                <div className="bg-blue-100 text-blue-900 font-bold rounded p-3 text-center min-w-[70px] mr-4" data-id="element-2201">
                  <div className="text-lg" data-id="element-2202">1981</div>
                  <div className="text-xs" data-id="element-2203">SET</div>
                </div>
                <div data-id="element-2204">
                  <h3 className="font-bold text-blue-900" data-id="element-2205">Class of 1981</h3>
                  <p className="text-sm text-gray-500" data-id="element-2206">142 registered alumni</p>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-2207">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3Hew6rxsK47JVpej3qfHsv/WhatsApp_Image_2025-11-10_at_20.52.09.jpg" alt="Class Representative" className="w-12 h-12 rounded-full object-cover mr-3" data-id="element-2208" />
                <div data-id="element-2209">
                  <h4 className="font-medium text-gray-900" data-id="element-2210">
                    Elder Lanre Oyesile
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-2211">Class Representative</p>
                </div>
              </div>
              <Button href="/alumni/sets" variant="outline" size="sm" fullWidth data-id="element-2212">
                View Class Directory
              </Button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2213">
              <div className="flex items-center mb-4" data-id="element-2214">
                <div className="bg-blue-100 text-blue-900 font-bold rounded p-3 text-center min-w-[70px] mr-4" data-id="element-2215">
                  <div className="text-lg" data-id="element-2216">1978</div>
                  <div className="text-xs" data-id="element-2217">SET</div>
                </div>
                <div data-id="element-2218">
                  <h3 className="font-bold text-blue-900" data-id="element-2219">Class of 1978</h3>
                  <p className="text-sm text-gray-500" data-id="element-2220">156 registered alumni</p>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-2221">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xoPZ5CeiJfFxam6WebTFFc/WhatsApp_Image_2025-11-09_at_14.37.11.jpg" alt="Class Representative" className="w-12 h-12 rounded-full object-cover mr-3" data-id="element-2222" />
                <div data-id="element-2223">
                  <h4 className="font-medium text-gray-900" data-id="element-2224">
                    Abimbola Taiwo Olarewaju
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-2225">Class Representative</p>
                </div>
              </div>
              <Button href="/alumni/sets" variant="outline" size="sm" fullWidth data-id="element-2226">
                View Class Directory
              </Button>
            </div>
          </div>
          <div className="mt-8 text-center" data-id="element-2227">
            <Button href="/alumni/sets" variant="secondary" icon={<ArrowRightIcon data-id="element-2229" />} iconPosition="right" data-id="element-2228">
              View All Graduation Sets
            </Button>
          </div>
        </section>

        {/* Upcoming Alumni Events */}
        <section className="mb-16" data-id="element-2230">
          <div className="text-center mb-10" data-id="element-2231">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2232">
              Upcoming Alumni Events
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2233"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2234">
              Stay connected with your alma mater and fellow alumni through our
              exciting events and gatherings.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2235">
            {alumniEvents.slice(0, 2).map(event => <div key={event.id} className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col md:flex-row" data-id="element-2236">
                <div className="md:w-2/5 h-48 md:h-auto" data-id="element-2237">
                  <img src={event.image} alt={event.title} className="w-full h-full object-cover" data-id="element-2238" />
                </div>
                <div className="md:w-3/5 p-6" data-id="element-2239">
                  <h3 className="text-xl font-bold text-blue-900 mb-3" data-id="element-2240">
                    {event.title}
                  </h3>
                  <div className="flex items-center text-gray-600 mb-2 text-sm" data-id="element-2241">
                    <CalendarIcon className="h-4 w-4 mr-2" data-id="element-2242" />
                    <span data-id="element-2243">{formatDate(event.date)}</span>
                  </div>
                  <div className="flex items-center text-gray-600 mb-2 text-sm" data-id="element-2244">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2245">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-2246" />
                    </svg>
                    <span data-id="element-2247">{event.time}</span>
                  </div>
                  <div className="flex items-center text-gray-600 mb-3 text-sm" data-id="element-2248">
                    <MapPinIcon className="h-4 w-4 mr-2" data-id="element-2249" />
                    <span data-id="element-2250">{event.location}</span>
                  </div>
                  <p className="text-gray-700 text-sm mb-4 line-clamp-2" data-id="element-2251">
                    {event.description}
                  </p>
                  <Button href="/alumni/events" variant="outline" size="sm" data-id="element-2252">
                    Event Details
                  </Button>
                </div>
              </div>)}
          </div>
          <div className="mt-8 text-center" data-id="element-2253">
            <Button href="/alumni/events" variant="primary" icon={<ArrowRightIcon data-id="element-2255" />} iconPosition="right" data-id="element-2254">
              View All Alumni Events
            </Button>
          </div>
        </section>

        {/* Alumni Achievements */}
        <section className="mb-16" data-id="element-2256">
          <div className="text-center mb-10" data-id="element-2257">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2258">
              Alumni Achievements
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2259"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2260">
              Celebrating the remarkable accomplishments of our alumni across
              various fields.
            </p>
          </div>
          <div className="bg-blue-900 text-white p-8 rounded-lg" data-id="element-2261">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center" data-id="element-2262">
              <div className="bg-blue-800/50 p-6 rounded-lg backdrop-blur-sm border border-blue-700/50 transform transition-transform hover:scale-105" data-id="element-2263">
                <div className="inline-block p-4 rounded-full bg-yellow-500/20 mb-4" data-id="element-2264">
                  <GraduationCapIcon className="h-8 w-8 text-yellow-400" data-id="element-2265" />
                </div>
                <div className="text-4xl font-bold text-yellow-400 mb-2" data-id="element-2266">
                  250+
                </div>
                <p className="text-lg" data-id="element-2267">Professors & Researchers</p>
              </div>
              <div className="bg-blue-800/50 p-6 rounded-lg backdrop-blur-sm border border-blue-700/50 transform transition-transform hover:scale-105" data-id="element-2268">
                <div className="inline-block p-4 rounded-full bg-yellow-500/20 mb-4" data-id="element-2269">
                  <BriefcaseIcon className="h-8 w-8 text-yellow-400" data-id="element-2270" />
                </div>
                <div className="text-4xl font-bold text-yellow-400 mb-2" data-id="element-2271">
                  500+
                </div>
                <p className="text-lg" data-id="element-2272">Business Leaders & Entrepreneurs</p>
              </div>
              <div className="bg-blue-800/50 p-6 rounded-lg backdrop-blur-sm border border-blue-700/50 transform transition-transform hover:scale-105" data-id="element-2273">
                <div className="inline-block p-4 rounded-full bg-yellow-500/20 mb-4" data-id="element-2274">
                  <TrophyIcon className="h-8 w-8 text-yellow-400" data-id="element-2275" />
                </div>
                <div className="text-4xl font-bold text-yellow-400 mb-2" data-id="element-2276">
                  75+
                </div>
                <p className="text-lg" data-id="element-2277">National Award Recipients</p>
              </div>
              <div className="bg-blue-800/50 p-6 rounded-lg backdrop-blur-sm border border-blue-700/50 transform transition-transform hover:scale-105" data-id="element-2278">
                <div className="inline-block p-4 rounded-full bg-yellow-500/20 mb-4" data-id="element-2279">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2280">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" data-id="element-2281" />
                  </svg>
                </div>
                <div className="text-4xl font-bold text-yellow-400 mb-2" data-id="element-2282">
                  30+
                </div>
                <p className="text-lg" data-id="element-2283">Government Officials</p>
              </div>
            </div>
          </div>
        </section>

        {/* Join Alumni Network CTA */}
        <section data-id="element-2284">
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-400 p-8 rounded-lg text-center" data-id="element-2285">
            <h2 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-2286">
              Join Our Alumni Network
            </h2>
            <p className="text-blue-900 mb-6 max-w-2xl mx-auto" data-id="element-2287">
              Stay connected with your alma mater and fellow alumni. Register to
              receive updates about alumni events, networking opportunities, and
              school developments.
            </p>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-2288">
              <Button href="/alumni/join" variant="primary" size="lg" data-id="element-2289">
                Register as an Alumnus
              </Button>
              <Button href="/contact" variant="outline" size="lg" className="border-blue-900 text-blue-900 hover:bg-blue-900/10" data-id="element-2290">
                Contact Alumni Office
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>;
};
export default Alumni;